// Schaduwen die op bovenvlakken van blokken vallen, geclipt op de footprint,
// voor 2D-weergave.
//
// In 2D top-down wordt elk blok als gekleurde footprint over `blocks-fill`
// getekend, wat de daaronder liggende grondschaduw overdekt of dimt. Twee
// gevallen, twee helpers:
//
//   computeRoofShadows       — voor blokken met heightTop > 0 (echte daken)
//   computeFlatBlockShadows  — voor blokken met heightTop === 0 (tuin,
//                              bestrating, plat gebouw)
//
// Beide leunen op `computeShadowsAt` (dezelfde routine die ook door het
// jaaraggregaat en de Haagse-norm-toets wordt gebruikt) en clippen via
// polygon-clipping op de footprint (L-vormen en concave polygonen worden
// correct afgehandeld).
//
// Performance: O(N·M) per helper. Voor typische scenario's (N,M ≲ 50) ruim
// binnen een frame per slider-tick.

import polygonClipping, { type MultiPolygon, type Ring } from "polygon-clipping";
import type { Block } from "../types/scenario";
import { computeShadowsAt } from "./projectShadow";
import type { SunPosition } from "./sunPosition";

export interface RoofShadowFeature {
  /** Polygoon op het dakvlak in [lng, lat]. */
  polygon: [number, number][];
  /** Blok wiens dak de schaduw ontvangt (z = heightTop van dit blok). */
  roofBlockId: string;
  /** Blok dat de schaduw werpt. */
  castingBlockId: string;
  /** 0–1: aandeel licht dat door het schaduwwerkende blok wordt geblokkeerd. */
  blockingFactor: number;
}

export interface FlatBlockShadowFeature {
  /** Polygoon op grondniveau in [lng, lat], geclipt op de footprint. */
  polygon: [number, number][];
  /** Blok (heightTop = 0) wiens footprint de schaduw ontvangt. */
  flatBlockId: string;
  /** Blok dat de schaduw werpt. */
  castingBlockId: string;
  /** 0–1: aandeel licht dat door het schaduwwerkende blok wordt geblokkeerd. */
  blockingFactor: number;
}

/**
 * Bereken voor elk blok met heightTop > 0 de schaduwen die op zijn bovenvlak
 * vallen, geclipt op de footprint. Onder MIN_ELEVATION_RAD geeft de helper
 * een lege array (dat regelt `computeShadowsAt` al).
 */
export function computeRoofShadows(
  blocks: Block[],
  sun: SunPosition,
): RoofShadowFeature[] {
  const out: RoofShadowFeature[] = [];

  for (const roof of blocks) {
    if (roof.heightTop <= 0) continue;

    const shadows = computeShadowsAt(blocks, sun, roof.heightTop);
    if (shadows.length === 0) continue;

    const roofRing = toClosedRing(roof.polygon);
    if (roofRing.length < 4) continue; // < 3 unieke vertices

    for (const f of shadows) {
      // Zelf-schaduw filteren conform yearAggregate.ts:189-194. `computeShadowsAt`
      // slaat dit blok normaal al over (effectiveTop = effectiveBottom), maar
      // defensief blijft de filter goed leesbaar.
      if (f.blockId === roof.id) continue;

      const shadowRing = toClosedRing(f.polygon);
      if (shadowRing.length < 4) continue;

      const clipped = clipShadowToFootprint(shadowRing, roofRing);
      for (const ring of clipped) {
        out.push({
          polygon: ring,
          roofBlockId: roof.id,
          castingBlockId: f.blockId,
          blockingFactor: f.blockingFactor,
        });
      }
    }
  }

  return out;
}

/**
 * Bereken voor elk blok met heightTop === 0 de grondschaduwen die op zijn
 * footprint vallen, geclipt op die footprint. Gebruikt om in 2D — wanneer de
 * heatmap uit staat — de schaduw ook duidelijk te tonen over tuinen,
 * bestrating en platte gebouwen heen (zonder de dimming van blocks-fill).
 *
 * De z=0-schaduwprojectie wordt één keer berekend en hergebruikt voor elk
 * ontvangend blok. Voor blokken met heightTop > 0 zie `computeRoofShadows`.
 */
export function computeFlatBlockShadows(
  blocks: Block[],
  sun: SunPosition,
): FlatBlockShadowFeature[] {
  // Welke blokken hebben überhaupt heightTop === 0? Zo niet, dan slaan we de
  // hele berekening over (en de z=0-shadow-call) — scheelt werk in scenario's
  // waar enkel hoge gebouwen staan.
  const flats = blocks.filter((b) => b.heightTop === 0);
  if (flats.length === 0) return [];

  const shadows = computeShadowsAt(blocks, sun, 0);
  if (shadows.length === 0) return [];

  const out: FlatBlockShadowFeature[] = [];
  for (const flat of flats) {
    const flatRing = toClosedRing(flat.polygon);
    if (flatRing.length < 4) continue;

    for (const f of shadows) {
      // Defensief: een blok met heightTop=0 werpt zelf geen schaduw (al
      // gefilterd in projectShadow.ts:124), dus deze guard is een no-op,
      // maar consistent met computeRoofShadows.
      if (f.blockId === flat.id) continue;

      const shadowRing = toClosedRing(f.polygon);
      if (shadowRing.length < 4) continue;

      const clipped = clipShadowToFootprint(shadowRing, flatRing);
      for (const ring of clipped) {
        out.push({
          polygon: ring,
          flatBlockId: flat.id,
          castingBlockId: f.blockId,
          blockingFactor: f.blockingFactor,
        });
      }
    }
  }
  return out;
}

/**
 * Sluit een ring (maak laatste punt = eerste punt) zoals polygon-clipping
 * en GeoJSON verlangen. Geeft een kopie terug.
 */
function toClosedRing(ring: [number, number][]): Ring {
  if (ring.length < 3) return ring.slice() as Ring;
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (last[0] === first[0] && last[1] === first[1]) return ring.slice() as Ring;
  return [...ring, [first[0], first[1]]] as Ring;
}

/**
 * Snij `shadow` met `footprint`. polygon-clipping kan een lege of meervoudige
 * uitkomst geven (bv. een schaduw die in twee stukken over een L-vorm ligt).
 * We vlakken de MultiPolygon af tot een lijst outer-rings — gaten (holes)
 * negeren we voor de huidige use case, want zichtbaar onderscheid in een
 * uniforme half-doorzichtige fill is verwaarloosbaar en het vereenvoudigt
 * de GeoJSON-output.
 */
function clipShadowToFootprint(shadow: Ring, footprint: Ring): [number, number][][] {
  let result: MultiPolygon;
  try {
    result = polygonClipping.intersection([shadow], [footprint]);
  } catch {
    // polygon-clipping kan op gedegenereerde input (zelf-snijdende polygonen)
    // gooien. Liever niets renderen dan crashen.
    return [];
  }
  const rings: [number, number][][] = [];
  for (const polygon of result) {
    if (polygon.length === 0) continue;
    rings.push(polygon[0].map((p) => [p[0], p[1]] as [number, number]));
  }
  return rings;
}
